import { ArrowForward } from "@mui/icons-material";
import { Button, CircularProgress, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import { useQuery } from "@tanstack/react-query";
import AuthInvalid from "components/auth-invalid";
import { useAuth } from "contexts/auth";
import React, { ChangeEvent, MouseEvent, useState } from "react";
import api from "services/api";
import { load } from "services/requests";
import { useStyles } from "./styles";

interface Data {
  id: number;
  TABELA_1: string;
  TABELA_1_KEY: string;
  CAMPO_1: string;
  CONTEUDO_1: string;
  TABELA_2: string;
  TABELA_2_KEY: string;
  CAMPO_2: string;
  CONTEUDO_2: string;
  TASK: string;
  DESCRICAO_ERRO: string;
  label: string;
}

type Order = "asc" | "desc";

interface HeadCell {
  id: keyof Data;
  label: string;
}

const headCells: readonly HeadCell[] = [
  {
    id: "label",
    label: "Diferença",
  },

  {
    id: "DESCRICAO_ERRO",
    label: "Tipo de Erro",
  },
];

const CadastralDifferences = () => {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof Data>("id");
  const [selected, setSelected] = useState<readonly number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { isAuthenticated } = useAuth();
  const classes = useStyles();

  const { data, isError, isPending } = useQuery({
    queryKey: ["conducco-cdp-organizations", "select-all-data"],
    queryFn: () =>
      load("/conducco-cdp-organizations/select-all-data", {
        tableName: "diferencas_cadastrais",
        rowsPerPage: 100,
      }),
  });

  if (!isAuthenticated && isError && isPending) {
    return <AuthInvalid />;
  }

  const result: Data[] =
    data &&
    data?.data?.data?.data?.map((elm: any, index: number) => ({
      ...elm,
      id: index + 1,
      label: "",
    }));

  const handleRequestSort = (
    event: MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = result.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: MouseEvent<unknown>, id: number) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: readonly number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected?.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const isSelected = (id: number) => selected.indexOf(id) !== -1;

  const onSubmit = async () => {
    setIsLoading(true);
    const dto = {
      keyName: result[0].CAMPO_1,
      aceites: result.map((item, index) => {
        const elm = selected.find((elm) => elm === index + 1);

        return {
          key: index + 1,
          value: elm ? item.CONTEUDO_2 : item.CONTEUDO_1,
          columnName: elm ? item.CAMPO_2 : item.CAMPO_1,
        };
      }),
    };

    await api
      .post("/conducco-cdp-organizations/aceites", dto)
      .then((response) => {
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  return (
    <Box className={classes.bodyContainer}>
      <Box sx={{ minWidth: "800px", maxWidth: "800px" }}>
        <TableContainer>
          <Table
            sx={{ width: "100%" }}
            aria-labelledby="tableTitle"
            size="medium"
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              rowCount={result?.length}
              numSelected={selected?.length}
              onRequestSort={handleRequestSort}
              onSelectAllClick={handleSelectAllClick}
              disabled={isLoading}
            />
            <TableBody>
              {result?.map((row, index) => {
                const isItemSelected = isSelected(row.id);
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow
                    hover
                    onClick={(event) => handleClick(event, row.id)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.id}
                    selected={isItemSelected}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        inputProps={{
                          "aria-labelledby": labelId,
                        }}
                      />
                    </TableCell>
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      padding="none"
                    >
                      <Box className={classes.containerLabel}>
                        <Typography>{row.CONTEUDO_1 || ""}</Typography>
                        <ArrowForward />
                        <Typography>{row.CONTEUDO_2 || ""}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="left">{row.DESCRICAO_ERRO}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box className={classes.buttonContainer}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          sx={{ height: "32px", padding: "0 2rem" }}
          onClick={onSubmit}
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress color="inherit" size={26} />
          ) : (
            "ENVIAR"
          )}
        </Button>
      </Box>
    </Box>
  );
};

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  disabled: boolean;
}

const EnhancedTableHead: React.FC<EnhancedTableProps> = ({
  onSelectAllClick,
  order,
  orderBy,
  numSelected,
  rowCount,
  onRequestSort,
  disabled,
}) => {
  const createSortHandler =
    (property: keyof Data) => (event: MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            disabled={disabled}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

export default CadastralDifferences;
